import { Currency } from '../../types/currency'
import { PriceActionWithEditMode } from '../../types/priceClass'

// DisplayRate is a SINK in the price-action chain: it reads the running rate at its
// position but does NOT advance the net currency chain, and it carries empty
// currencies (fromCurrency/toCurrency are ''). So when validating currency
// continuity, the relevant neighbour of a net action is the nearest adjacent
// NON-DisplayRate action — not the immediate one.
//
// Without skipping the sink, a Discount placed right after a DisplayRate compares its
// fromCurrency against the sink's empty '' toCurrency, which never matches, so the
// row is flagged with a (false) currency error and Save is disabled. These helpers
// centralise the sink-skipping so the add / edit / drag-reorder validators agree.

// Nearest prior NON-DisplayRate action's toCurrency, else the chain's input currency.
export function prevNetOutput(
  items: PriceActionWithEditMode[],
  index: number,
  inputCurrency: Currency
): Currency {
  for (let j = index - 1; j >= 0; j--) {
    if (items[j].priceAction.actionType !== 'DisplayRate') {
      return items[j].priceAction.actionAmount.toCurrency
    }
  }
  return inputCurrency
}

// Nearest later NON-DisplayRate action's fromCurrency, else the chain's output currency.
export function nextNetInput(
  items: PriceActionWithEditMode[],
  index: number,
  outputCurrency: Currency
): Currency {
  for (let j = index + 1; j < items.length; j++) {
    if (items[j].priceAction.actionType !== 'DisplayRate') {
      return items[j].priceAction.actionAmount.fromCurrency
    }
  }
  return outputCurrency
}

// Net output currency of the chain: the last NON-DisplayRate action's toCurrency, else
// the input currency. (A trailing DisplayRate sink must not be treated as the output.)
export function netOutputCurrency(
  items: PriceActionWithEditMode[],
  inputCurrency: Currency
): Currency {
  for (let j = items.length - 1; j >= 0; j--) {
    if (items[j].priceAction.actionType !== 'DisplayRate') {
      return items[j].priceAction.actionAmount.toCurrency
    }
  }
  return inputCurrency
}

// A "transform" advances the net chain — anything that is NOT a DisplayRate sink and NOT a
// Discount. The "Discount must come first" rule only forbids a Discount AFTER a transform;
// leading DisplayRate sinks AND leading Discounts are both allowed (this mirrors the backend's
// seenNonDiscount rule, which permits multiple discounts at the start of a class).
export function hasTransformBefore(
  items: PriceActionWithEditMode[],
  index: number
): boolean {
  for (let j = 0; j < index; j++) {
    const t = items[j].priceAction.actionType
    if (t !== 'DisplayRate' && t !== 'Discount') {
      return true
    }
  }
  return false
}

export function recalcPriceActionErrors(
  items: PriceActionWithEditMode[],
  inputCurrency: Currency,
  outputCurrency: Currency
): boolean[] {
  return items.map((item, index, arr) => {
    const current = item.priceAction
    const prevOutput = prevNetOutput(arr, index, inputCurrency)
    const nextInput = nextNetInput(arr, index, outputCurrency)

    const currencyError =
      current.actionType !== 'DisplayRate' &&
      Boolean(
        (prevOutput && prevOutput !== current.actionAmount.fromCurrency) ||
          (nextInput && nextInput !== current.actionAmount.toCurrency)
      )

    const discountOrderError =
      current.actionType === 'Discount' && hasTransformBefore(arr, index)

    const passOnCommissionError =
      current.actionType === 'CommissionPassOn' &&
      current.actionAmount.toCurrency !== outputCurrency

    return Boolean(currencyError || discountOrderError || passOnCommissionError)
  })
}
